/**
 * Columna "Compu" del Panel People (pedido KP 14/09): qué compu tiene asignada
 * el ingreso o, si no tiene, asignarle una disponible de su clapbase sin salir
 * del panel. Misma regla y misma mutación que Aprobaciones
 * (`approvals.assignDeviceFromStock`): la compu queda con owner = su mail y en
 * "Lista para Entregar" para el board de entregas del Office Manager.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Home, Laptop, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import type { PeopleFlowAltaStatus, PeopleFlowEquipment } from '@/../../shared/types/people-flow';

function AssignDeviceDialog({
  alta,
  equipment,
  open,
  onOpenChange,
}: {
  alta: PeopleFlowAltaStatus['alta'];
  equipment: PeopleFlowEquipment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const k = 'users.people_panel.equipment';
  const assign = trpc.approvals.assignDeviceFromStock.useMutation({
    onSuccess: (r) => {
      toast.success(t(`${k}.assigned_toast`, { modelo: r.modelo, serie: r.nroSerie, nombre: alta.nombre }));
      utils.peopleFlow.getEquipmentContext.invalidate();
      utils.approvals.getApprovalsContext.invalidate();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e.message || t(`${k}.assign_error`)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(`${k}.dialog_title`, { nombre: alta.nombre })}</DialogTitle>
          <DialogDescription>
            {t(`${k}.dialog_desc`, { office: equipment.officeLabel, clapbase: alta.comunidad || '—' })}
          </DialogDescription>
        </DialogHeader>
        {!equipment.hasOffice && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            <Home className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{t(`${k}.no_office`)}</span>
          </div>
        )}
        {equipment.devices.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t(`${k}.none_available`)}</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {equipment.devices.map((d) => (
              <li key={d.deviceId} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <div className="font-medium">{d.modelo || t(`${k}.no_model`)}</div>
                  <div className="text-xs text-muted-foreground">
                    {d.nroSerie && <span className="font-mono">{d.nroSerie}</span>}
                    {d.nroSerie && ' · '}
                    {d.status}
                  </div>
                </div>
                <Button
                  size="sm"
                  className="h-7 gap-1 text-xs"
                  disabled={assign.isPending}
                  onClick={() =>
                    assign.mutate({
                      deviceId: d.deviceId,
                      employeeEmail: alta.correoWinclap,
                      employeeName: alta.nombre,
                    })
                  }
                >
                  {assign.isPending ? <WinclapLoader size={12} /> : <UserPlus className="h-3 w-3" />}
                  {t(`${k}.assign_this`)}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-foreground">{t(`${k}.after_assign`)}</p>
      </DialogContent>
    </Dialog>
  );
}

export function EquipmentCell({
  alta,
  equipment,
  canAssign,
}: {
  alta: PeopleFlowAltaStatus['alta'];
  equipment: PeopleFlowEquipment | undefined;
  canAssign: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const k = 'users.people_panel.equipment';

  if (!equipment) return <span className="text-xs text-muted-foreground">…</span>;

  if (equipment.assigned) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs" title={equipment.assigned.nroSerie}>
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <span className="truncate">{equipment.assigned.modelo || t(`${k}.assigned_no_model`)}</span>
      </span>
    );
  }

  // Sin correo Winclap no hay a quién asignar: la cuenta la crea el flujo de altas.
  if (!alta.correoWinclap.includes('@')) {
    return <span className="text-xs text-muted-foreground">{t(`${k}.no_email`)}</span>;
  }

  if (equipment.available === 0) {
    return (
      <Link
        to="/admin/inventory"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
        title={t(`${k}.no_stock_hint`, { office: equipment.officeLabel })}
      >
        <Laptop className="h-3.5 w-3.5" />
        {t(`${k}.no_stock`)}
      </Link>
    );
  }

  if (!canAssign) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Laptop className="h-3.5 w-3.5" />
        {t(`${k}.available_count`, { count: equipment.available })}
      </span>
    );
  }

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-7 gap-1 text-xs"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <UserPlus className="h-3 w-3" />
        {t(`${k}.assign_button`, { count: equipment.available })}
      </Button>
      {open && (
        <div onClick={(e) => e.stopPropagation()}>
          <AssignDeviceDialog alta={alta} equipment={equipment} open={open} onOpenChange={setOpen} />
        </div>
      )}
    </>
  );
}
